// ADIRATH — Partners page
import React from 'react';
import { Reveal, SectionHead } from './primitives';
import { PageHero } from './serviceData';
import { Icon } from './Icon';
import { CTASection } from './HomeSectionsB';

/* ── Toshiba partner data ─────────────────────────────────────────────── */
const TOSHIBA = {
  objectives: [
    'Development of advanced battery packs using Toshiba SCiB™ (LTO) cells',
    'Assembly and testing of high-performance energy storage systems',
    'Addressing the growing demand in the Indian market across multiple sectors',
  ],
  highlights: [
    ['zap',           'SCiB™ LTO Technology',    'Ultra-fast charging, extreme cycle life and superior safety — Toshiba\'s hallmark lithium titanate oxide cell chemistry.'],
    ['shield-check',  'Certified Safety',         'SCiB™ cells deliver the highest abuse tolerance in the industry, meeting the most demanding safety standards globally.'],
    ['repeat',        'Long Cycle Life',           'Over 20,000 charge-discharge cycles — engineered for applications where longevity and reliability are non-negotiable.'],
    ['globe',         'Global Standards',          'Joint development aligned with international IEC, MIL and rail/marine certification frameworks.'],
  ],
  gallery: [
    { src: '/photos/toshiba-mou1.webp', alt: 'EEE Energy & Toshiba India MoU signing' },
    { src: '/photos/toshiba-mou2.webp', alt: 'Toshiba collaboration event' },
  ],
};

export function PartnersPage({ onNavigate }) {
  return (
    <div>
      <PageHero
        kicker="Our Partners"
        crumbs={['Partners']}
        onNavigate={onNavigate}
        image="co-facility"
        title="Powering the future with global collaboration."
        sub="Strategic alliances with world-leading technology companies — bringing the best of global innovation to every ADIRATH solution."
      />

      {/* ── Partner nav strip ──────────────────────────────────────────── */}
      <section style={{ padding: '28px 0', background: 'var(--surface-inverse)', borderBottom: '1px solid var(--glass-dark-border)' }}>
        <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginRight: 8 }}>Partners</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 20px', borderRadius: 'var(--radius-pill)', background: 'rgba(67,196,67,0.12)', border: '1px solid rgba(67,196,67,0.3)', fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 13.5, color: 'var(--energy-bright)' }}>
            <Icon name="handshake" style={{ width: 15, height: 15 }} /> Toshiba
          </span>
        </div>
      </section>

      {/* ── Toshiba — hero intro ───────────────────────────────────────── */}
      <section style={{ padding: 'var(--section-y) 0', background: 'var(--surface-page)' }}>
        <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)' }}>
          <div style={{ display: 'grid', gap: 64, alignItems: 'center' }} className="adr-split">
            {/* Left — text */}
            <Reveal variant="up">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                {/* Logos */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                  <img src="/photos/toshiba-logo.webp" alt="Toshiba" style={{ height: 40, objectFit: 'contain' }} />
                </div>

                <div>
                  <span style={{ display: 'inline-block', fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 10 }}>Strategic MoU</span>
                  <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 'clamp(1.7rem, 3vw, 2.6rem)', letterSpacing: '-0.03em', lineHeight: 1.1, color: 'var(--text-strong)', margin: '0 0 18px' }}>
                    Strategic Collaboration with <span style={{ color: 'var(--accent)' }}>Toshiba</span>
                  </h2>
                  <p style={{ fontSize: 16, color: 'var(--text-body)', lineHeight: 1.75, margin: '0 0 14px' }}>
                    <strong>EEE Energy Private Limited</strong> has entered into a strategic MoU with <strong>Toshiba India Private Limited</strong> to collaborate on the development, assembly, and testing of advanced lithium battery packs using <em>Toshiba's SCiB™</em> technology.
                  </p>
                  <p style={{ fontSize: 15.5, color: 'var(--text-body)', lineHeight: 1.75, margin: 0 }}>
                    This milestone strengthens our global alignment in delivering safe, high-performance, and fast-charging energy storage solutions — marking the beginning of a long-term journey toward innovation in energy storage.
                  </p>
                </div>

                {/* Quote */}
                <blockquote style={{ margin: 0, padding: '20px 24px', borderLeft: '3px solid var(--accent)', background: 'var(--accent-soft)', borderRadius: '0 var(--radius-lg) var(--radius-lg) 0' }}>
                  <p style={{ margin: 0, fontStyle: 'italic', fontSize: 15, color: 'var(--text-body)', lineHeight: 1.7 }}>
                    "This collaboration with Toshiba reinforces our vision to be a global leader in advanced lithium battery solutions, while enabling cutting-edge innovation in mobility, automation, and energy storage."
                  </p>
                </blockquote>
              </div>
            </Reveal>

            {/* Right — gallery */}
            <Reveal variant="scale">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {TOSHIBA.gallery.map(({ src, alt }) => (
                  <div key={src} className="adr-hov-img" style={{ borderRadius: 'var(--radius-xl)', overflow: 'hidden', boxShadow: 'var(--shadow-lg)' }}>
                    <img src={src} alt={alt} style={{ width: '100%', display: 'block', objectFit: 'cover', maxHeight: 260 }} />
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Objectives ────────────────────────────────────────────────── */}
      <section style={{ padding: 'var(--section-y) 0', background: 'var(--surface-inverse)', color: '#fff' }}>
        <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)' }}>
          <SectionHead dark eyebrow="Objective of the Collaboration" title="A framework built for scalable energy innovation." max={640} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 40, maxWidth: 760 }}>
            {TOSHIBA.objectives.map((obj, oi) => (
              <Reveal key={oi} variant="up" delay={oi * 0.07}>
                <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', padding: '20px 24px', background: 'var(--glass-dark-bg)', border: '1px solid var(--glass-dark-border)', borderRadius: 'var(--radius-xl)' }}>
                  <span style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 10, background: 'rgba(67,196,67,0.15)', color: 'var(--energy-bright)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 13 }}>
                    {String(oi + 1).padStart(2, '0')}
                  </span>
                  <p style={{ margin: 0, fontSize: 15.5, color: 'rgba(255,255,255,0.85)', lineHeight: 1.65 }}>{obj}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Highlights ────────────────────────────────────────────────── */}
      <section style={{ padding: 'var(--section-y) 0', background: 'var(--surface-page)' }}>
        <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)' }}>
          <SectionHead eyebrow="SCiB™ Technology" title="Why Toshiba SCiB™ sets the standard." max={560} />
          <div className="adr-highlights-grid" style={{ display: 'grid', gap: 22, marginTop: 40 }}>
            {TOSHIBA.highlights.map(([icon, title, desc], hi) => (
              <Reveal key={title} variant="up" delay={hi * 0.07}>
                <div className="adr-hov-lift" style={{ display: 'flex', gap: 18, padding: '26px 24px', background: 'var(--surface-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-sm)' }}>
                  <span style={{ flexShrink: 0, width: 48, height: 48, borderRadius: 13, background: 'var(--accent-soft)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon name={icon} style={{ width: 22, height: 22 }} />
                  </span>
                  <div>
                    <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 17, color: 'var(--text-strong)', marginBottom: 8 }}>{title}</div>
                    <p style={{ margin: 0, fontSize: 14.5, color: 'var(--text-body)', lineHeight: 1.65 }}>{desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection onNavigate={onNavigate} />
    </div>
  );
}
